import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { ContactAddressPage, LandingPage, PersonalInfoPage } from './components/Home';
import StreamlinedSignup from './components/StreamlinedSignup';
import OtpVerification from './components/OtpVerification';
import PasswordSetup from './components/PasswordSetup';
import PortalRoutes from './routes/PortalRoutes';
import './App.css';
import "../src/Assets/styles.css";
import "../src/Assets/index.css";
import MainLayout from './components/Layout/MainLayout';
import PasswordCreation from './components/PasswordCreation';
import SignInPage from './components/SignIn';
import { Toaster } from 'react-hot-toast';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import SidebarLayout from './components/Layout/SideBarLayout';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DrawerProvider, useDrawer } from './contexts/DrawerContext';
import RightDrawer from './components/Layout/RightDrawer';
import ForgotPasswordPage from './components/ForgotPasswordPage';
import { UserProvider } from './contexts/UserContext';
import { useEffect, useRef } from 'react';
import { FirebaseMessagingProvider } from './contexts/FirebaseMessagingContext';
import { NotificationsProvider } from './contexts/NotificationsContext';
import { VideoCallProvider } from './contexts/VideoCallContext';
import { IncomingCallProvider } from './contexts/IncomingCallContext';
import ServiceWorkerListener from './components/ServiceWorkerListener';
import TriviaPage from './components/Pages/Trivia';
import TriviaPopUp from './components/Layout/TriviaPopUp';
import { useDailyTrivia } from './utils/useDailyTrivia';

const greenTheme = createTheme({
  palette: {
    primary: {
      main: '#2e7d32', // rich green
    },
    secondary: {
      main: '#66bb6a', // soft green
    },
  },
});

function AppContent() {
  const { admissionDetailsDrawerOpen, toggleAdmissionDetailsDrawer } = useDrawer();
  const timerRef = useRef(null);
  const { showPopup, triviaQuestions, handleLetsGo, handleSkip, markTriviaAsCompleted } = useDailyTrivia();
  const navigate = useNavigate();
  const location = useLocation();
  const publicRoutes = ['/login', '/create-account', '/forgot-password', '/verify-otp', '/signup-full', '/'];
  const isPublicRoute = publicRoutes.includes(location.pathname);

  // Handle auto logout
  const handleLogout = () => {
    console.log('Auto logout triggered due to inactivity');
    sessionStorage.clear();
    localStorage.clear();
    window.location.href = '/login';
  };

  // Reset timer on activity
  const resetTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      handleLogout();
    }, 3600000); // 1 hour = 3600000ms
  };

  // Track user activity
  useEffect(() => {
    const activityEvents = ['mousemove', 'keydown', 'mousedown', 'scroll', 'touchstart'];

    // Only start timer if user is logged in (has userId in session)
    const userId = sessionStorage.getItem("userId");
    if (userId) {
      console.log('✅ Initializing inactivity timer for logged in user');
      activityEvents.forEach(event => window.addEventListener(event, resetTimer));
      resetTimer(); // Initialize on load
    }

    return () => {
      activityEvents.forEach(event => window.removeEventListener(event, resetTimer));
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  // REMOVED: Duplicate Firebase initialization from here
  // The FirebaseMessagingProvider will handle all Firebase messaging initialization

  return (
    <>
      {/* ServiceWorkerListener must be inside Router but before Routes */}
      <ServiceWorkerListener />
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 5000,
          success: {
            style: {
              background: '#fff',
              color: '#2e7d32',
            },
            iconTheme: {
              primary: '#fff',
              secondary: '#2e7d32',
            },
          },
          error: {
            style: {
              background: '#d32f2f',
              color: '#fff',
            },
            iconTheme: {
              primary: '#fff',
              secondary: '#d32f2f',
            },
          },
          loading: {
            style: {
              background: '#fff',
              color: '#2e7d32',
            },
          },
          custom: {
            style: {
              background: '#fff',
              color: '#2e7d32',
              padding: '16px',
            },
          },
        }}
      />
       {!isPublicRoute && showPopup && (
          <TriviaPopUp
            onLetsGo={() => handleLetsGo(navigate)}
            onSkip={handleSkip}
          />
        )}
      <Routes>
       
        <Route path="/" element={<LandingPage />} />
        <Route path="/create-account" element={<StreamlinedSignup />} />
        <Route path="/verify-otp" element={<OtpVerification />} />
        <Route path="/create-password" element={<PasswordSetup />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/contact-address" element={<ContactAddressPage />} />
        <Route path="/passwords" element={<PasswordCreation />} />
        <Route path="/login" element={<SignInPage />} />
        <Route element={<SidebarLayout />}>
          <Route path="/*" element={<PortalRoutes />} />
          <Route path="/trivia" element={<TriviaPage questions={triviaQuestions} onTriviaComplete={markTriviaAsCompleted} />} />

        </Route>
      </Routes>
      <RightDrawer
        open={admissionDetailsDrawerOpen}
        onClose={toggleAdmissionDetailsDrawer(false)}
      />
    </>
  );
}

export default function App() {
  return (
    <ThemeProvider theme={greenTheme}>
      <UserProvider>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <NotificationsProvider>
            {/* FirebaseMessagingProvider MUST come before IncomingCallProvider */}
            <FirebaseMessagingProvider>
              <VideoCallProvider>
                <IncomingCallProvider>
                  <DrawerProvider>
                    <Router>
                      <AppContent />
                    </Router>
                  </DrawerProvider>
                </IncomingCallProvider>
              </VideoCallProvider>
            </FirebaseMessagingProvider>
          </NotificationsProvider>
        </LocalizationProvider>
      </UserProvider>
    </ThemeProvider>
  );
}